import React, { useState, useRef, useMemo } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from '@floating-ui/react';
import { Input } from '@/components/ui/input';
import { User } from 'lucide-react';

export interface Client {
  id_usuario: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
}

interface Props {
  clientes: Client[];
  placeholder?: string;
  onSelect?: (cliente: Client) => void;
  value?: string;
  onChange?: (v: string) => void;
  displayField?: 'nombre' | 'telefono' | 'email';
}

export const ClientSearchBox: React.FC<Props> = ({
  clientes,
  placeholder = 'Buscar cliente...',
  onSelect,
  value = '',
  onChange,
  displayField = 'nombre',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Contenedor donde se monta el desplegable. Si estamos dentro de un Dialog
  // de Radix, debe ser el propio DialogContent: Radix marca como inerte
  // (pointer-events/aria-hidden) todo lo que queda FUERA del contenido del
  // modal, así que un portal en document.body no recibiría click ni scroll.
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);

  const filtered = useMemo(() => {
    if (value.trim().length < 1) return [];
    const term = value.toLowerCase();
    return clientes.filter(
      (c) =>
        (c.nombre || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (c.telefono || '').toLowerCase().includes(term)
    );
  }, [value, clientes]);

  const open = isFocused && filtered.length > 0;

  const { refs, floatingStyles } = useFloating({
    open,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(192, availableHeight)}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  // Combina la ref de Floating UI (reference) con la búsqueda del
  // DialogContent contenedor para el portal. Lo resolvemos aquí, al montar
  // el input, para que portalRoot ya esté disponible en el primer render
  // del desplegable (si esperáramos a open=true, el primer render caería en
  // document.body —zona inerte del modal— y no respondería hasta el segundo).
  const setReference = (node: HTMLInputElement | null) => {
    anchorRef.current = node;
    refs.setReference(node);
    if (node) {
      const dialog = node.closest('[role="dialog"]') as HTMLElement | null;
      setPortalRoot(dialog ?? null);
    }
  };

  const handleSelect = (c: Client) => {
    const valor =
      (c as any)[displayField] ?? c.nombre ?? c.email ?? c.telefono ?? '';
    onChange?.(valor);
    onSelect?.(c);
    setIsFocused(false);
  };

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => setIsFocused(false), 150);
  };

  return (
    <>
      <Input
        ref={setReference}
        value={value}
        placeholder={placeholder}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setIsFocused(true);
        }}
        onBlur={handleBlur}
        onChange={(e) => {
          // Si el usuario escribe, sigue enfocado: cancelamos cualquier
          // cierre pendiente por blur y reafirmamos el foco.
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setIsFocused(true);
          onChange?.(e.target.value);
        }}
      />

      {open && (
        // root = DialogContent cuando estamos dentro de un modal (si no,
        // Floating UI usa document.body por defecto). Floating UI posiciona
        // correctamente aunque ese contenedor tenga transform.
        <FloatingPortal root={portalRoot ?? undefined}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[9999] bg-white dark:bg-[#1c1c1e] border border-gray-200 dark:border-white/10 rounded-md shadow-lg overflow-y-auto overscroll-contain"
          >
            {filtered.map((c) => (
              <div
                key={c.id_usuario}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                  handleSelect(c);
                }}
                className="p-2 flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {c.nombre || '(Sin nombre)'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {c.email || '-'} | {c.telefono || '-'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};